import React, { useState, useEffect, useRef, useContext } from "react";
import { useHistory, useParams } from "react-router-dom";
import { parseISO, format, isSameDay, isValid } from "date-fns";
import clsx from "clsx";

// Helper function to safely parse and format dates
const safeFormatDate = (dateValue, todayFormat = "HH:mm", otherFormat = "dd/MM") => {
  if (!dateValue) return "";

  try {
    let date;
    if (dateValue instanceof Date) {
      date = dateValue;
    } else if (typeof dateValue === "string") {
      date = parseISO(dateValue);
    } else if (typeof dateValue === "number") {
      date = new Date(dateValue);
    } else {
      return "";
    }

    if (!isValid(date)) return "";
    return isSameDay(date, new Date())
      ? format(date, todayFormat)
      : format(date, otherFormat);
  } catch {
    return "";
  }
};

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import MarkdownWrapper from "../MarkdownWrapper";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";

const TicketListItem = ({ ticket }) => {
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const { ticketId } = useParams();
  const isMounted = useRef(true);
  const { user } = useContext(AuthContext);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleAcceptTicket = async (id) => {
    setLoading(true);
    try {
      await api.put(`/tickets/${id}`, {
        status: "open",
        userId: user?.id,
      });
    } catch (err) {
      setLoading(false);
      toastError(err);
    }
    if (isMounted.current) {
      setLoading(false);
    }
    history.push(`/tickets/${id}`);
  };

  const handleSelectTicket = (id) => {
    history.push(`/tickets/${id}`);
  };

  const isSelected = ticketId && +ticketId === ticket.id;
  const isPending = ticket.status === "pending";
  const isGroupTicket = ticket.isGroup === true;

  return (
    <div
      className={clsx(
        "relative flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors",
        isSelected
          ? "bg-primary-50 dark:bg-primary-900/20"
          : "hover:bg-dark-50 dark:hover:bg-dark-800/50",
        isPending && !isGroupTicket && "cursor-default"
      )}
      onClick={() => {
        // Grupos são sempre clicáveis, tickets pending não são (exceto se for grupo)
        if (!isPending || isGroupTicket) {
          handleSelectTicket(ticket.id);
        }
      }}
    >
      {/* Queue Color Indicator */}
      <div
        className="absolute left-0 top-0 bottom-0 w-1 rounded-r"
        style={{ backgroundColor: ticket.queue?.color || "#7C7C7C" }}
        title={ticket.queue?.name || "Sem fila"}
      />

      {/* Avatar */}
      <div className="relative flex-shrink-0 ml-1">
        {ticket?.contact?.profilePicUrl ? (
          <img
            src={ticket.contact.profilePicUrl}
            alt={ticket.contact.name}
            className="w-10 h-10 rounded-full object-cover"
          />
        ) : (
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white font-semibold text-sm">
            {ticket.contact?.name?.charAt(0)?.toUpperCase() || "?"}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 overflow-hidden">
        {/* Top Row - Name and Time */}
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm text-dark-900 dark:text-white truncate flex-1">
            {ticket.contact?.name}
          </span>
          {ticket.lastMessage && (
            <span className="text-[11px] text-dark-400 dark:text-dark-500 flex-shrink-0">
              {safeFormatDate(ticket.updatedAt)}
            </span>
          )}
        </div>

        {/* Bottom Row - Message Preview */}
        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-xs text-dark-500 dark:text-dark-400 truncate flex-1">
            {ticket.lastMessage ? (
              <MarkdownWrapper>{ticket.lastMessage}</MarkdownWrapper>
            ) : (
              <span className="italic">Sem mensagens</span>
            )}
          </span>

          {/* Unread Badge */}
          {ticket.unreadMessages > 0 && (
            <span className="flex-shrink-0 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-bold text-white bg-success-500 rounded-full">
              {ticket.unreadMessages > 99 ? "99+" : ticket.unreadMessages}
            </span>
          )}
        </div>

        {/* Connection and Queue Tags - Below message */}
        <div className="mt-1 flex items-center gap-1.5 flex-wrap">
          {/* Connection Tag */}
          {ticket.whatsappId && (
            <span
              className="inline-block text-[10px] px-1.5 py-0.5 bg-primary-500 text-white rounded truncate"
              title={i18n.t("ticketsList.connectionTitle")}
            >
              {ticket.whatsapp?.name}
            </span>
          )}

          {/* Queue Tag */}
          <span
            className="inline-block text-[10px] px-1.5 py-0.5 rounded truncate"
            style={{
              backgroundColor: ticket.queue?.color || "#9e9e9e",
              color: "#ffffff"
            }}
            title={ticket.queue?.name || "Sem fila"}
          >
            {ticket.queue?.name || "Sem fila"}
          </span>
        </div>
      </div>

      {/* Right side - Accept Button or Status */}
      <div className="flex-shrink-0 flex flex-col items-end gap-1">
        {/* Accept Button for Pending Tickets (não mostrar para grupos) */}
        {isPending && !isGroupTicket && (
          <button
            className={clsx(
              "px-2.5 py-1 text-xs font-medium rounded-md transition-colors",
              "bg-success-500 text-white hover:bg-success-600",
              loading && "opacity-50 cursor-not-allowed"
            )}
            disabled={loading}
            onClick={(e) => {
              e.stopPropagation();
              handleAcceptTicket(ticket.id);
            }}
          >
            {loading ? (
              <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            ) : (
              i18n.t("ticketsList.buttons.accept")
            )}
          </button>
        )}

        {/* Group Badge */}
        {isGroupTicket && (
          <span className="text-[10px] px-1.5 py-0.5 bg-success-100 dark:bg-success-900/30 text-success-700 dark:text-success-400 rounded">
            {i18n.t("ticketsList.groupsHeader")}
          </span>
        )}

        {/* Closed Badge */}
        {ticket.status === "closed" && !isGroupTicket && (
          <span className="text-[10px] px-1.5 py-0.5 bg-dark-300 dark:bg-dark-600 text-dark-600 dark:text-dark-300 rounded">
            Fechado
          </span>
        )}
      </div>
    </div>
  );
};

export default TicketListItem;
